var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StatusPermission, UserType } from '../../enum';
export var useUserAccessState = function (_a) {
  var open = _a.open,
    userType = _a.userType,
    isBpo = _a.isBpo,
    accessData = _a.accessData,
    userOrgData = _a.userOrgData,
    clients = _a.clients,
    allLobs = _a.allLobs,
    t = _a.t;
  var accessPrefilledRef = useRef(false);
  // Step 1 - access selections
  var _b = useState([]),
    selectedClientIds = _b[0],
    setSelectedClientIds = _b[1];
  var _c = useState(new Set()),
    selectedLobKeys = _c[0],
    setSelectedLobKeys = _c[1];
  var _d = useState({}),
    step1Errors = _d[0],
    setStep1Errors = _d[1];
  // Step 2 - org hierarchy permissions
  var _e = useState(StatusPermission.View),
    orgConfigPermission = _e[0],
    setOrgConfigPermission = _e[1];
  var _f = useState({}),
    clientPermissions = _f[0],
    setClientPermissions = _f[1];
  var _g = useState({}),
    lobPermissions = _g[0],
    setLobPermissions = _g[1];
  var defaultPerm = Number(userType) === UserType.Viewer ? StatusPermission.View : StatusPermission.Edit;
  // LOBs for selected clients (BPO) or all LOBs (non-BPO)
  var relevantLobs = useMemo(function () {
    if (isBpo) return allLobs.filter(function (l) {
      return l.clientId !== null && selectedClientIds.includes(l.clientId);
    });
    return allLobs;
  }, [allLobs, isBpo, selectedClientIds]);
  // Save disabled if every top-level item is set to Hide
  var allPermsHidden = useMemo(function () {
    if (isBpo) {
      return selectedClientIds.length > 0 && selectedClientIds.every(function (cid) {
        var _a;
        return ((_a = clientPermissions[cid]) !== null && _a !== void 0 ? _a : defaultPerm) === StatusPermission.Hide;
      });
    }
    return selectedLobKeys.size > 0 && Array.from(selectedLobKeys).every(function (k) {
      var _a;
      return ((_a = lobPermissions[k]) !== null && _a !== void 0 ? _a : defaultPerm) === StatusPermission.Hide;
    });
  }, [isBpo, selectedClientIds, selectedLobKeys, clientPermissions, lobPermissions, defaultPerm]);
  // Pre-fill access state from existing user access (edit mode).
  // Wait for dropdown options to load and only run once so late API responses
  // don't overwrite manual selections.
  useEffect(function () {
    if (!accessData || accessPrefilledRef.current) return;
    var cls = accessData.clients,
      lobs = accessData.lobs;
    if (isBpo && cls.length > 0 && clients.length === 0) return;
    if (lobs.length > 0 && allLobs.length === 0) return;
    // Don't overwrite if the user already made Step 1 selections before access data loaded.
    if (selectedClientIds.length > 0) {
      accessPrefilledRef.current = true;
      return;
    }
    accessPrefilledRef.current = true;
    var clientIds = cls.map(function (c) {
      return c.clientId;
    }).filter(function (id) {
      return !isBpo || clients.some(function (c) {
        return c.id === id;
      });
    });
    var lobKeys = new Set(lobs.filter(function (l) {
      return allLobs.some(function (row) {
        return row.id === l.lobId && row.clientId === l.clientId;
      });
    }).map(function (l) {
      return "".concat(l.clientId, "_").concat(l.lobId);
    }));
    setSelectedClientIds(clientIds);
    setSelectedLobKeys(lobKeys);
    var cPerms = {};
    cls.forEach(function (c) {
      if (clientIds.includes(c.clientId)) {
        cPerms[c.clientId] = c.permission;
      }
    });
    setClientPermissions(cPerms);
    var lPerms = {};
    lobs.forEach(function (l) {
      var key = "".concat(l.clientId, "_").concat(l.lobId);
      if (lobKeys.has(key)) {
        lPerms[key] = l.permission;
      }
    });
    setLobPermissions(lPerms);
  }, [accessData, clients, allLobs, isBpo, selectedClientIds]);
  useEffect(function () {
    if (!isBpo || clients.length === 0) return;
    setSelectedClientIds(function (prev) {
      if (prev.length === 0) return prev;
      var next = prev.filter(function (id) {
        return clients.some(function (c) {
          return c.id === id;
        });
      });
      return next.length === prev.length ? prev : next;
    });
  }, [isBpo, clients]);
  // Pre-fill org config permission from user data
  useEffect(function () {
    if (userOrgData === null || userOrgData === void 0 ? void 0 : userOrgData.orgConfigPermission) {
      setOrgConfigPermission(userOrgData.orgConfigPermission);
    }
  }, [userOrgData]);
  // Remove LOB selections when their client is deselected (BPO)
  useEffect(function () {
    if (isBpo) {
      setSelectedLobKeys(function (prev) {
        if (prev.size === 0) return prev;
        var next = new Set();
        prev.forEach(function (key) {
          var clientId = Number.parseInt(key.split('_')[0]);
          if (selectedClientIds.includes(clientId)) next.add(key);
        });
        return next.size === prev.size ? prev : next;
      });
    }
  }, [selectedClientIds, isBpo]);
  // Clear Step 1 inline errors when selections change
  useEffect(function () {
    setStep1Errors({});
  }, [selectedClientIds]);
  // Downgrade hierarchy permissions when switching to Viewer
  useEffect(function () {
    if (open && Number(userType) === UserType.Viewer) {
      if (orgConfigPermission === StatusPermission.Edit) {
        setOrgConfigPermission(StatusPermission.View);
      }
      setClientPermissions(function (prev) {
        var next = {};
        for (var _i = 0, _a = Object.entries(prev); _i < _a.length; _i++) {
          var _b = _a[_i],
            k = _b[0],
            v = _b[1];
          next[Number(k)] = v === StatusPermission.Edit ? StatusPermission.View : v;
        }
        return next;
      });
      setLobPermissions(function (prev) {
        var next = {};
        for (var _i = 0, _a = Object.entries(prev); _i < _a.length; _i++) {
          var _b = _a[_i],
            k = _b[0],
            v = _b[1];
          next[k] = v === StatusPermission.Edit ? StatusPermission.View : v;
        }
        return next;
      });
    }
  }, [userType, open]); // eslint-disable-line react-hooks/exhaustive-deps
  var validateStep1 = useCallback(function () {
    var validationErrors = {};
    if (isBpo && selectedClientIds.length === 0 && clients.length > 0) {
      validationErrors.clients = t('validation.clientRequired') || 'At least one client is required';
    }
    setStep1Errors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [selectedClientIds, isBpo, clients, t]);
  var buildAccessPayload = useCallback(function () {
    var lobsPayload = Array.from(selectedLobKeys).map(function (lobKey) {
      var _a;
      var _b = lobKey.split('_'),
        clientIdStr = _b[0],
        lobIdStr = _b[1];
      var clientId = Number.parseInt(clientIdStr);
      var lobId = Number.parseInt(lobIdStr);
      var lobPerm = (_a = lobPermissions[lobKey]) !== null && _a !== void 0 ? _a : defaultPerm;
      return {
        clientId: clientId,
        lobId: lobId,
        permission: lobPerm
      };
    });
    var clientsPayload = selectedClientIds.map(function (clientId) {
      var _a;
      return {
        clientId: clientId,
        permission: (_a = clientPermissions[clientId]) !== null && _a !== void 0 ? _a : defaultPerm
      };
    });
    return {
      clients: isBpo ? clientsPayload : undefined,
      lobs: lobsPayload
    };
  }, [selectedClientIds, selectedLobKeys, clientPermissions, lobPermissions, isBpo, defaultPerm]);
  /** Expand LOB keys and initialize default permissions for Step 2.
   *  Called when transitioning from Step 1 to Step 2. */
  var initializeStep2Permissions = useCallback(function () {
    var allLobKeys = new Set(relevantLobs.map(function (l) {
      return "".concat(l.clientId, "_").concat(l.id);
    }));
    setSelectedLobKeys(allLobKeys);
    var newClientPerms = __assign({}, clientPermissions);
    selectedClientIds.forEach(function (cid) {
      if (!newClientPerms[cid]) newClientPerms[cid] = defaultPerm;
    });
    setClientPermissions(newClientPerms);
    var newLobPerms = __assign({}, lobPermissions);
    relevantLobs.forEach(function (l) {
      var key = "".concat(l.clientId, "_").concat(l.id);
      if (!newLobPerms[key]) newLobPerms[key] = defaultPerm;
    });
    setLobPermissions(newLobPerms);
  }, [relevantLobs, clientPermissions, lobPermissions, selectedClientIds, defaultPerm]);
  var handleCascadeClientPerm = useCallback(function (clientId, perm) {
    setClientPermissions(function (prev) {
      var _a;
      return __assign(__assign({}, prev), (_a = {}, _a[clientId] = perm, _a));
    });
    // Hide only affects the client itself — LOBs no longer support Hide, so leave their
    // Edit/View permissions untouched (they're simply not shown while the client is Hidden).
    if (perm === StatusPermission.Hide) return;
    var newLobPerms = {};
    Array.from(selectedLobKeys).forEach(function (lobKey) {
      if (Number.parseInt(lobKey.split('_')[0]) === clientId) {
        newLobPerms[lobKey] = perm;
      }
    });
    setLobPermissions(function (prev) {
      return __assign(__assign({}, prev), newLobPerms);
    });
  }, [selectedLobKeys]);
  var handleCascadeLobPerm = useCallback(function (clientId, lobId, perm) {
    var lobKey = "".concat(clientId, "_").concat(lobId);
    setLobPermissions(function (prev) {
      var _a;
      return __assign(__assign({}, prev), (_a = {}, _a[lobKey] = perm, _a));
    });
  }, []);
  /** Reset all access state — called on dialog close */
  var resetAccessState = useCallback(function () {
    accessPrefilledRef.current = false;
    setSelectedClientIds([]);
    setSelectedLobKeys(new Set());
    setStep1Errors({});
    setClientPermissions({});
    setLobPermissions({});
    setOrgConfigPermission(StatusPermission.View);
  }, []);
  /** Clear selections when switching companies (superadmin, new user) */
  var clearSelectionsForOrgChange = useCallback(function () {
    setSelectedClientIds([]);
    setSelectedLobKeys(new Set());
  }, []);
  return {
    selectedClientIds: selectedClientIds,
    setSelectedClientIds: setSelectedClientIds,
    selectedLobKeys: selectedLobKeys,
    setSelectedLobKeys: setSelectedLobKeys,
    step1Errors: step1Errors,
    orgConfigPermission: orgConfigPermission,
    setOrgConfigPermission: setOrgConfigPermission,
    clientPermissions: clientPermissions,
    lobPermissions: lobPermissions,
    defaultPerm: defaultPerm,
    allPermsHidden: allPermsHidden,
    validateStep1: validateStep1,
    buildAccessPayload: buildAccessPayload,
    initializeStep2Permissions: initializeStep2Permissions,
    handleCascadeClientPerm: handleCascadeClientPerm,
    handleCascadeLobPerm: handleCascadeLobPerm,
    resetAccessState: resetAccessState,
    clearSelectionsForOrgChange: clearSelectionsForOrgChange
  };
};